import React from 'react';
import PropTypes from 'prop-types';
import { change } from 'redux-form';
import { createSafeDynamicRegex } from '../../utils/library';
import { makeApiCall } from '../../redux/actions/common/common-action';
import Select, { components } from 'react-select';
import find from 'lodash/find';
import uniqBy from 'lodash/uniqBy';
import map from 'lodash/map';
import { FLAG_URL } from '../../utils/constants';
import COLORS from '../../common/styles/color-pallete';

const caseInsensitive = (query) => new RegExp(createSafeDynamicRegex(query || '').trim(), 'gi');
const phoneCode = /[+]\d*\s/g;

function inferPropsValue(value) {
  return value === undefined || value;
}

function defaultValue(values, placeholder) {
  return [{ key: '', value: undefined, text: placeholder }].concat(values);
}

function sortSuggestions({ text: first }, { text: second }) {
  return first.replace(phoneCode, '').localeCompare(second.replace(phoneCode, ''));
}

function searchSuggestion(options, query) {
  const regex = caseInsensitive(query);
  return options
    .filter(({ text }) => text.toString().search(regex) > -1)
    .sort(({ text: first }, { text: second }) => {
      first = first.toString();
      second = second.toString();
      return (
        first.search(regex) - second.search(regex) ||
        first.replace(phoneCode, '').localeCompare(second.replace(phoneCode, ''))
      );
    });
}

const colourStyles = {
  control: (styles) => ({
    ...styles,
    border: 'none',
    borderRadius: 0,
    borderColor: '#fff',
    boxShadow: 'none',
    borderBottom: '1px solid #e0e0e0',
    ':hover': {
      borderBottom: '1px solid #e0e0e0'
    }
  }),
  input: (styles) => ({
    ...styles,
    caretColor: COLORS.brandPrimary
  }),
  indicatorSeparator: (styles) => ({ ...styles, display: 'none' }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#fff',
    zIndex: 9999,
    marginTop: 1
  }),
  multiValue: (styles) => ({
    ...styles,
    '.css-14qqzf3': {
      backgroundColor: '#e0e0e0',
      cursor: 'pointer'
    }
  }),
  container: (styles, { selectProps }) => ({
    ...styles,
    paddingTop: 12,
    height: Math.max(50, selectProps.dynamicHeight),
    border: 'none'
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    backgroundColor: '#e0e0e0',
    ':hover': {
      backgroundColor: 'rgba(72, 249, 254, 0.4)',
      cursor: 'pointer'
    }
  }),
  placeholder: (styles) => ({ ...styles, color: '#A9A9A9', paddingLeft: 0 }),
  menuList: (styles) => ({
    ...styles,
    fontFamily: 'hellix-regular',
    fontSize: '16px',
    color: COLORS.black,
    padding: '8px 0 4px 3px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    width: '100%'
  }),
  clearIndicator: (styles) => ({ ...styles, display: 'none' }),
  option: (styles, { isFocused }) => ({
    ...styles,
    paddingLeft: 10,
    fontSize: 16,
    height: 40,
    paddingTop: 14,
    border: isFocused ? '1px solid rgba(72, 249, 254, 0.4)' : '1px solid white',
    backgroundColor: isFocused ? 'rgba(72, 249, 254, 0.4)' : 'white',
    ':hover': {
      backgroundColor: 'rgba(72, 249, 254, 0.4)'
    }
  })
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <i
        aria-hidden="true"
        style={{ paddingLeft: 17, color: '#000000', fontSize: '1em' }}
        className="dropdown icon "
      />
    </components.DropdownIndicator>
  );
};

class MultiSelectCommonDropdown extends React.Component {
  httpRequest = async (url) => {
    try {
      const response = await makeApiCall({
        method: 'get',
        url: url,
        withCredentials: true
      });
      if (response && response.data && response.data.success) {
        const {
          multiple,
          preselect_if_one_item,
          preselect_and_disable_if_one_item,
          placeholder,
          optional,
          fieldName,
          formName
        } = this.props;
        let { bindToThisObject } = this.props;
        const data = this.props.showImage
          ? this.appendImageInData(response.data.data)
          : response.data.data;
        let options = this.props.sort || false ? data.sort(sortSuggestions) : data;
        if (options) {
          options = options.map((key) => {
            key['label'] = key['text'];
            return key;
          });
        }
        if (bindToThisObject && options && !this.value) {
          bindToThisObject = bindToThisObject.map((val) => {
            if (typeof val === 'string') {
              return find(options, { value: val });
            }
            return val;
          });
          this.props.dispatch(change(formName, fieldName, uniqBy(bindToThisObject, 'value')));
        }
        if (!optional || multiple || preselect_if_one_item || preselect_and_disable_if_one_item)
          return this.setState({
            options: (this.options = this.searchOptions = options)
          });
        return this.setState({
          options: (this.options = defaultValue((this.searchOptions = options), placeholder))
        });
      }
    } catch (error) {
      //eslint-disable-next-line
      console.error(error);
    }
  };

  appendImageInData = (data) => {
    return data.map(function (obj) {
      //Note  :coded as mages will be directly saved in images folder only
      //Just in case if you want different path then pass path as prop
      // Eg : imagePath={(obj) => `/images/hello/hiee${obj.value.toLowerCase()}.png`}
      obj.image = {
        avatar: true,
        src: `${FLAG_URL}${obj.value.toLowerCase()}.png`
      };
      return obj;
    });
  };

  UNSAFE_componentWillReceiveProps = async (nextProps) => {
    const {
      multiple,
      preselect_if_one_item,
      preselect_and_disable_if_one_item,
      staticValues,
      placeholder,
      dependedOnObject,
      url,
      optional
    } = nextProps;
    if (staticValues && staticValues !== this.props.staticValues) {
      if (!optional || multiple || preselect_if_one_item || preselect_and_disable_if_one_item)
        this.setState({
          options: (this.options =
            this.props.sort || false ? staticValues.sort(sortSuggestions) : staticValues)
        });
      else
        this.setState({
          options: (this.options = defaultValue(
            (this.searchOptions =
              this.props.sort || false ? staticValues.sort(sortSuggestions) : staticValues),
            placeholder
          ))
        });
      return this.preSelectIfOneItem(staticValues);
    }

    //If dependedOnObject is changed, then fire http request with new url
    if (dependedOnObject === false) return;
    if (
      dependedOnObject !== this.props.dependedOnObject &&
      url &&
      (url !== this.props.url || !this.state.options.length)
    ) {
      await this.httpRequest(url);
    }

    // If dropdown is having only one value they it will be set due to preselect_if_one_item flag
    this.preSelectIfOneItem();
  };

  componentDidMount = async () => {
    const {
      url,
      dependedOnObject,
      multiple,
      preselect_if_one_item,
      preselect_and_disable_if_one_item,
      staticValues,
      placeholder,
      optional
    } = this.props;
    if (url) {
      if (dependedOnObject === false) return;
      await this.httpRequest(url);
    }

    if (staticValues) {
      //staticValues : defaultValue(staticValues, placeholder)
      const options = this.props.sort || false ? staticValues.sort(sortSuggestions) : staticValues;
      if (!optional || multiple || preselect_if_one_item || preselect_and_disable_if_one_item)
        this.setState({
          options: (this.options = this.searchOptions = options)
        });
      else
        this.setState({
          options: (this.options = defaultValue((this.searchOptions = options), placeholder))
        });
    }

    // If dropdown is having only one value they it will be set due to preselect_if_one_item flag
    this.preSelectIfOneItem(staticValues);
  };

  preSelectIfOneItem = (staticValues) => {
    if (
      this.props.preselect_and_disable_if_one_item === true ||
      this.props.preselect_if_one_item === true
    ) {
      const { options } = this.state;
      let disabledVals = [];
      if (options.length === 1 || (staticValues && staticValues.length === 1)) {
        let setvalue;
        if (staticValues && staticValues.length === 1) {
          setvalue = staticValues[0].value;
        } else {
          setvalue = options[0].value;
        }
        this.props.dispatch(change(this.props.formName, this.props.fieldName, setvalue));
        const { onPreFilled } = this.props;
        if (onPreFilled) {
          onPreFilled(options[0]);
        }
      } else if (
        options &&
        // eslint-disable-next-line no-prototype-builtins
        options.find((x) => x.hasOwnProperty('disabled')) &&
        (disabledVals = options.filter((x) => !x.disabled)) &&
        disabledVals.length === 1
      ) {
        //handle disabled values, if only one value is not disabled, prefilled that value
        const setvalue = disabledVals[0].value;
        this.props.dispatch(change(this.props.formName, this.props.fieldName, setvalue));
        const { onPreFilled } = this.props;
        if (onPreFilled) onPreFilled(disabledVals[0]);
      } else if (this.props.bindToThisObject && !this.props.emptyOnNotFound) {
        const found = options.find((k) => k.value === this.props.bindToThisObject);
        if (!found) {
          this.props.dispatch(change(this.props.formName, this.props.fieldName, ''));
        }
      }
    }
  };

  isDropdownDisabled = () => {
    if (this.props.preselect_and_disable_if_one_item === true && this.state.options.length === 1) {
      return true;
    } else return this.props.disabled;
  };

  handleChange = (data) => {
    const {
        onDropdownChange,
        optional,
        multiple,
        preselect_if_one_item,
        preselect_and_disable_if_one_item,
        selectOnChange
      } = this.props,
      value = data;
    this.value = value;

    if (optional && !(multiple || preselect_if_one_item || preselect_and_disable_if_one_item)) {
      this.setState({ className: 'clearable', options: this.options });
    }

    //This onChange function is of redux-form it takes value OR event and it will set in redux-form
    if (selectOnChange || multiple) {
      // donot reflect in redux if select on close is active
      this.props.onChange(this.value);
    }
    onDropdownChange &&
      onDropdownChange(
        data && data.length > 1
          ? data.map((key) => {
              return key.value;
            })
          : data
      );
  };

  handleClose = () => {
    const {
        onDropdownClose,
        preselect_and_disable_if_one_item,
        preselect_if_one_item,
        multiple,
        optional
      } = this.props,
      value = this.value;

    if (optional && !(preselect_if_one_item || preselect_and_disable_if_one_item)) {
      this.setState({ className: 'clearable', options: this.options });
    }

    if (!multiple) {
      this.props.onChange(
        this.value || (this.value !== '' ? this.props.bindToThisObject : this.value)
      );
    }
    onDropdownClose &&
      onDropdownClose(
        value && value.length > 1
          ? value.map((key) => {
              return key.value;
            })
          : value
      );
  };

  onSearchChange = (event, { searchQuery }) => {
    const text = searchQuery || '';
    this.setState({
      className: text ? '' : 'clearable',
      options: text ? this.searchOptions : this.options
    });
  };

  constructor(props) {
    super(props);
    this.state = { options: [], className: 'clearable' };
    this.options = [];
    this.searchOptions = [];
    this.value = null;
  }

  render() {
    const {
      multiple,
      bindToThisObject,
      preselect_if_one_item,
      preselect_and_disable_if_one_item,
      optional,
      onBlur,
      getOptions,
      code,
      id,
      placeholder,
      isSearchable,
      inputRef,
      enableThis,
      fieldName
    } = this.props;
    let value;
    if (getOptions) getOptions(code, this.state.options);
    if (preselect_and_disable_if_one_item && this.state.options.length === 1) {
      value = this.state.options[0].value;
    } else {
      // needs to be discussed with Ayesha
      if (enableThis)
        value =
          this.value ||
          (bindToThisObject ? (multiple ? [].concat(bindToThisObject) : bindToThisObject) : null);
      else
        value = bindToThisObject
          ? multiple
            ? [].concat(bindToThisObject)
            : bindToThisObject
          : null;
    }
    if (
      value &&
      value.length > 0 &&
      !value[0].key &&
      this.state.options &&
      this.state.options.length > 0
    ) {
      value = map(value, (val) => find(this.state.options, { value: val }));
    }
    return (
      <Select
        id={id}
        placeholder={placeholder}
        defaultValue={[{ key: '', value: undefined, text: placeholder }]}
        value={value}
        ref={inputRef}
        components={{ DropdownIndicator }}
        isSearchable={inferPropsValue(isSearchable) && searchSuggestion}
        onChange={this.handleChange}
        closeMenuOnSelect={false}
        maxMenuHeight={200}
        onMenuClose={this.handleClose}
        isMulti={true}
        options={this.state.options}
        onBlur={onBlur}
        styles={colourStyles}
        disabled={this.isDropdownDisabled()}
        dynamicHeight={value && value.length > 0 ? value.length * 7.5 : 0}
        name={fieldName}
        className={
          (optional &&
            !(multiple || preselect_if_one_item || preselect_and_disable_if_one_item) &&
            this.state.className) ||
          ''
        }
        onInputChange={(() => {
          if (optional && !(multiple || preselect_if_one_item || preselect_and_disable_if_one_item))
            return this.onSearchChange;
          return null;
        })()}
      />
    );
  }
}

MultiSelectCommonDropdown.propTypes = {
  onChange: PropTypes.func,
  bindToThisObject: PropTypes.any,
  placeholder: PropTypes.string,
  url: PropTypes.string,
  staticValues: PropTypes.array,
  onDropdownChange: PropTypes.func,
  onPreFilled: PropTypes.func,
  dependedOnObject: PropTypes.any,
  isUpward: PropTypes.bool,
  id: PropTypes.string,
  isSearchable: PropTypes.bool,
  showImage: PropTypes.bool,
  getOptions: PropTypes.func,
  selectOnChange: PropTypes.bool,
  code: PropTypes.string,
  emptyOnNotFound: PropTypes.bool,
  //Here for dependency ,
  //two properties are created(dependedOnObject AND isDepended) as dependedOnObject can be undefined,
  //so i din't want to fire http request when dependedOnObject is undefined
  //Eg : when form loads the property on which object is depended is always undefined
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  showLoader: PropTypes.bool,
  fluid: PropTypes.bool,
  isCompact: PropTypes.bool,
  onDropdownClose: PropTypes.func,
  preselect_if_one_item: PropTypes.bool,
  preselect_and_disable_if_one_item: PropTypes.bool,
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  fieldName: PropTypes.string,
  changeLabel: PropTypes.string,
  optional: PropTypes.bool,
  onBlur: PropTypes.func,
  sort: PropTypes.bool,
  inputRef: PropTypes.func,
  enableThis: PropTypes.bool
};

export default MultiSelectCommonDropdown;
